import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, CheckCircle2, ArrowRight, Phone, Gift, Copy, Share2 } from "lucide-react";
import { format } from "date-fns";
import { BOOKING, CALLBACK } from "@/constants/testIds";
import { toast } from "sonner";

const homeSizes = [
  { value: "studio", label: "Studio" },
  { value: "1br", label: "1 Bedroom" },
  { value: "2br", label: "2 Bedroom" },
  { value: "3br", label: "3 Bedroom" },
  { value: "4br_plus", label: "4+ Bedroom" },
  { value: "office", label: "Office / Commercial" },
];
const moveTypes = [
  { value: "local", label: "Local Move" },
  { value: "long_distance", label: "Long Distance" },
  { value: "freight_container", label: "Load/Unload Container or Trailer" },
  { value: "storage", label: "Storage Only" },
  { value: "commercial", label: "Commercial" },
  { value: "specialty", label: "Specialty" },
  { value: "packing", label: "Packing Only" },
];
const allServices = ["packing", "unpacking", "piano", "antiques", "storage_30", "appliance_install", "stairs"];
const serviceLabels = {
  packing: "Packing",
  unpacking: "Unpacking",
  piano: "Piano",
  antiques: "Antiques",
  storage_30: "30-day Storage",
  appliance_install: "Appliance Install",
  stairs: "Stairs",
};

export default function BookingSection() {
  const [tab, setTab] = useState("book"); // book | callback
  return (
    <section id="book" className="py-24 lg:py-32 bg-[#223932] text-white" data-testid="booking-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <div className="overline text-[#5BB239]">Book Your Move</div>
          <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Pick a date.<br />We'll do the rest.
          </h2>
          <p className="mt-6 text-white/70 leading-relaxed max-w-md">
            Lock in your moving day with our crew. We'll follow up within 2 business hours to
            confirm timing, walk through inventory, and finalize your written quote.
          </p>

          <div className="mt-10 flex gap-2 border border-white/15 p-1 w-fit">
            <TabBtn active={tab === "book"} onClick={() => setTab("book")} testid="tab-book">
              Full Booking
            </TabBtn>
            <TabBtn active={tab === "callback"} onClick={() => setTab("callback")} testid="tab-callback">
              Request Callback
            </TabBtn>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-4">
            <Stat n="2 hrs" label="Avg. Response" />
            <Stat n="Same-week" label="Availability" />
            <Stat n="Free" label="Walkthroughs" />
            <Stat n="0" label="Hidden Fees" />
          </div>
        </div>

        <div className="lg:col-span-3">
          {tab === "book" ? <BookingForm /> : <CallbackForm />}
        </div>
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 font-mono-label text-[11px] transition-colors ${
        active ? "bg-[#5BB239] text-white" : "text-white/60 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-l-2 border-[#5BB239] pl-3">
      <div className="font-display font-black text-2xl">{n}</div>
      <div className="font-mono-label text-[9px] text-white/50 mt-1">{label}</div>
    </div>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    move_type: "local",
    home_size: "2br",
    origin_city: "",
    origin_state: "KY",
    destination_city: "",
    destination_state: "KY",
    distance_miles: "",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [success, setSuccess] = useState(null);
  const successRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [referralInfo, setReferralInfo] = useState(null); // { valid, referrer_first_name, discount }
  const [referralChecking, setReferralChecking] = useState(false);

  // Auto-fill referral code from ?ref=XXX query string (from a shared link)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, []);

  // Debounced validation whenever code changes
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setReferralInfo(null);
      return;
    }
    setReferralChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/referrals/validate", { code });
        setReferralInfo(data);
      } catch {
        setReferralInfo({ valid: false, reason: "error" });
      } finally {
        setReferralChecking(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggle = (s) =>
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const handleDateSelect = (d) => {
    setDate(d || null);
    setFormError("");
    if (d) setDateOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!date) {
      const m = "Please pick a moving date.";
      setFormError(m);
      toast.error(m);
      return;
    }
    setLoading(true);
    try {
      const { distance_miles, ...rest } = form;
      const { data } = await api.post("/bookings", {
        ...rest,
        move_date: format(date, "yyyy-MM-dd"),
        services,
        sms_opt_in: smsOptIn,
        referral_code: referralCode.trim().toUpperCase(),
      });
      setSuccess(data);
      toast.success("Booking received! We'll be in touch within 2 hours.");
      // Make sure the customer actually sees the success card — long forms can leave them scrolled past it
      setTimeout(() => {
        successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Booking failed";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        ref={successRef}
        className="bg-[#F5F3EC] text-[#151816] p-10 border-l-4 border-[#5BB239] scroll-mt-24"
        data-testid={BOOKING.success}
      >
        <CheckCircle2 className="w-10 h-10 text-[#5BB239]" />
        <h3 className="mt-4 font-display font-black text-3xl tracking-tight">
          You're on the books.
        </h3>
        <p className="mt-3 text-[#5C635F] max-w-md">
          We've received your booking — our dispatcher will call you within 2 business hours to
          confirm details and lock in your crew.
        </p>
        <div className="mt-6 bg-white border border-[#DDD8C9] p-5">
          <div className="font-mono-label text-[10px] text-[#5C635F]">Confirmation ID</div>
          <div
            className="mt-1 font-display font-black text-3xl tracking-[0.15em]"
            data-testid="booking-confirmation-id"
          >
            {success.id.slice(0, 8).toUpperCase()}
          </div>
          <p className="mt-2 text-xs text-[#5C635F]">
            Save this. Use it with your email at{" "}
            <a href="/track" className="text-[#5BB239] font-bold hover:underline">
              /track
            </a>{" "}
            to check your move status anytime.
          </p>
        </div>
        <div className="mt-4 bg-[#EBE8DD] p-5">
          <div className="font-mono-label text-[10px] text-[#5C635F]">Estimated Range</div>
          <div className="mt-1 font-display font-black text-3xl">
            ${success.estimated_quote.low.toLocaleString()} — ${success.estimated_quote.high.toLocaleString()}
          </div>
          {success.referral_discount > 0 && (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-2 py-1 text-[11px] font-mono-label uppercase tracking-widest">
              <Gift className="w-3 h-3" /> ${success.referral_discount} Referral Discount Applied
            </div>
          )}
        </div>

        {/* Referral Share Card */}
        {success.referral_code_owned && (
          <ReferralShareCard code={success.referral_code_owned} name={form.full_name} />
        )}
        <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-5" data-testid="booking-deposit-notice">
          <div className="font-mono-label text-[10px] text-amber-700 tracking-widest">Action Required · $100 Deposit</div>
          <div className="mt-2 font-display font-black text-xl text-[#151816]">
            Watch your inbox for a QuickBooks invoice.
          </div>
          <p className="mt-2 text-sm text-[#5C635F] leading-relaxed">
            We'll send a <strong>$100 deposit invoice</strong> to your email within 30 minutes.
            Once paid, your date is officially <strong>LOCKED</strong> — the deposit is applied to your final bill.
            No payment, no date hold.
          </p>
        </div>
        <div className="mt-4 border border-[#DDD8C9] p-5 text-xs leading-relaxed">
          <div className="font-mono-label text-[10px] text-[#5BB239] mb-2">What To Expect Next</div>
          <ul className="space-y-1.5 text-[#5C635F]">
            <li>• Dispatcher calls within 2 business hours to confirm</li>
            <li>• $100 QuickBooks invoice hits your inbox — pay to lock the date</li>
            <li>• Final payment due at end of move · cash, check, Zelle preferred</li>
            <li>• Card payments add a 3.5% processing fee</li>
          </ul>
        </div>
        <a
          href="/track"
          className="mt-5 inline-flex items-center gap-2 bg-[#5BB239] text-white px-5 py-3 font-bold uppercase tracking-wider text-xs hover:bg-[#499828] transition-colors"
          data-testid="booking-success-track-link"
        >
          Track My Move
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#F5F3EC] text-[#151816] p-6 lg:p-10 space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name">
          <Input value={form.full_name} onChange={setE("full_name")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.fullName} />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={setE("phone")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.phone} />
        </Field>
      </div>
      <Field label="Email">
        <Input type="email" value={form.email} onChange={setE("email")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.email} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Move Type">
          <Select value={form.move_type} onValueChange={set("move_type")}>
            <SelectTrigger className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.moveType}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {moveTypes.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Home Size">
          <Select value={form.home_size} onValueChange={set("home_size")}>
            <SelectTrigger className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.homeSize}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {homeSizes.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="From (City)" className="sm:col-span-2">
          <Input value={form.origin_city} onChange={setE("origin_city")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.originCity} />
        </Field>
        <Field label="State">
          <Input value={form.origin_state} onChange={setE("origin_state")} required className="rounded-none border-[#DDD8C9] h-12 uppercase" data-testid={BOOKING.originState} />
        </Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="To (City)" className="sm:col-span-2">
          <Input value={form.destination_city} onChange={setE("destination_city")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={BOOKING.destCity} />
        </Field>
        <Field label="State">
          <Input value={form.destination_state} onChange={setE("destination_state")} required className="rounded-none border-[#DDD8C9] h-12 uppercase" data-testid={BOOKING.destState} />
        </Field>
      </div>

      <Field label="Moving Date">
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={BOOKING.moveDate}
              className="w-full h-12 border border-[#DDD8C9] bg-white px-3 flex items-center gap-2 text-sm text-left hover:bg-[#F5F3EC] transition-colors"
            >
              <CalendarIcon className="w-4 h-4 text-[#5BB239]" />
              {date ? (
                <span data-testid="booking-move-date-value">{format(date, "PPP")}</span>
              ) : (
                <span className="text-[#5C635F]">Pick a date</span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#F5F3EC]" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </Field>

      <Field label="Add-on Services">
        <div className="grid sm:grid-cols-3 gap-2">
          {allServices.map((s) => (
            <label key={s} className="flex items-center gap-2 border border-[#DDD8C9] p-2.5 cursor-pointer hover:bg-white transition-colors">
              <Checkbox checked={services.includes(s)} onCheckedChange={() => toggle(s)} className="rounded-none" />
              <span className="text-xs">{serviceLabels[s]}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="Notes (optional)">
        <Textarea value={form.notes} onChange={setE("notes")} rows={3} className="rounded-none border-[#DDD8C9]" data-testid={BOOKING.notes} placeholder="Stairs? Elevator? Heavy items? Tell us anything that helps." />
      </Field>

      <label className="flex items-start gap-3 border border-[#DDD8C9] p-4 cursor-pointer hover:bg-white transition-colors">
        <Checkbox
          checked={smsOptIn}
          onCheckedChange={(v) => setSmsOptIn(!!v)}
          className="rounded-none mt-0.5"
          data-testid="booking-sms-optin"
        />
        <span className="text-xs leading-relaxed">
          <strong className="font-display font-bold text-sm">Text me booking updates</strong>
          <span className="block mt-0.5 text-[#5C635F]">
            Send me an SMS when my move status changes (scheduled, complete, etc.). Standard
            message rates apply. We never sell your number.
          </span>
        </span>
      </label>

      {formError && (
        <div
          role="alert"
          data-testid="booking-error"
          className="bg-red-50 border-l-2 border-red-500 px-3 py-2 text-sm text-red-700"
        >
          {formError}
        </div>
      )}

      {/* Referral code input */}
      <div className="border border-dashed border-[#5BB239]/40 bg-[#5BB239]/5 p-4">
        <Label className="font-mono-label text-[10px] tracking-widest flex items-center gap-1.5">
          <Gift className="w-3 h-3 text-[#5BB239]" /> Referral Code (optional)
        </Label>
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="ALICE-8850"
            className="rounded-none border-[#DDD8C9] h-10 text-sm uppercase tracking-widest font-mono"
            data-testid="booking-referral-input"
          />
          {referralChecking && (
            <span className="font-mono-label text-[10px] text-[#5C635F]">Checking…</span>
          )}
        </div>
        {referralInfo && referralInfo.valid && (
          <div
            className="mt-2 flex items-center gap-2 text-emerald-700"
            data-testid="referral-valid-msg"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="text-xs">
              <strong>${referralInfo.discount} off</strong> — referred by {referralInfo.referrer_first_name}
            </span>
          </div>
        )}
        {referralInfo && !referralInfo.valid && referralCode.trim() && !referralChecking && (
          <div className="mt-2 text-xs text-red-600" data-testid="referral-invalid-msg">
            Not a valid code. Leave blank if you don&apos;t have one.
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#5BB239] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid={BOOKING.submit}
      >
        {loading ? "Booking…" : "Confirm My Booking"}
        <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}

function CallbackForm() {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", best_time: "", message: "" });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const setE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/callbacks", form);
      setSuccess(true);
      toast.success("Callback requested — we'll ring you back shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-[#F5F3EC] text-[#151816] p-10 border-l-4 border-[#5BB239]" data-testid={CALLBACK.success}>
        <Phone className="w-10 h-10 text-[#5BB239]" />
        <h3 className="mt-4 font-display font-black text-3xl tracking-tight">We'll call you.</h3>
        <p className="mt-3 text-[#5C635F]">A team member will reach out within 2 business hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#F5F3EC] text-[#151816] p-6 lg:p-10 space-y-5">
      <Field label="Full Name">
        <Input value={form.full_name} onChange={setE("full_name")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={CALLBACK.fullName} />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Phone">
          <Input value={form.phone} onChange={setE("phone")} required className="rounded-none border-[#DDD8C9] h-12" data-testid={CALLBACK.phone} />
        </Field>
        <Field label="Email (optional)">
          <Input type="email" value={form.email} onChange={setE("email")} className="rounded-none border-[#DDD8C9] h-12" data-testid={CALLBACK.email} />
        </Field>
      </div>
      <Field label="Best time to call">
        <Input value={form.best_time} onChange={setE("best_time")} placeholder="e.g. weekdays after 5pm" className="rounded-none border-[#DDD8C9] h-12" data-testid={CALLBACK.bestTime} />
      </Field>
      <Field label="What can we help with?">
        <Textarea value={form.message} onChange={setE("message")} rows={4} className="rounded-none border-[#DDD8C9]" data-testid={CALLBACK.message} />
      </Field>
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-[#5BB239] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] transition-colors disabled:opacity-60"
        data-testid={CALLBACK.submit}
      >
        {loading ? "Sending…" : "Request a Callback"}
      </button>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-mono-label text-[10px] text-[#5C635F]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}


function ReferralShareCard({ code, name }) {
  const [copied, setCopied] = useState(false);
  const firstName = (name || "").split(" ")[0] || "Friend";
  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/?ref=${code}#book`;
  const shareText = `Just booked with ThemBoysMoving & Storage. Use my code ${code} at booking and we both get $25 off your move.`;
  const fullShare = `${shareText} ${shareUrl}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — long-press to select");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "ThemBoysMoving referral",
          text: shareText,
          url: shareUrl,
        });
      } catch (e) {
        if (e && e.name === "AbortError") return;
        copy(fullShare, "Message");
      }
    } else {
      copy(fullShare, "Message");
    }
  };

  return (
    <div
      className="mt-4 relative overflow-hidden bg-gradient-to-br from-[#5BB239] to-[#3d8624] text-white p-6 lg:p-7"
      data-testid="referral-share-card"
    >
      <div className="absolute -right-6 -top-6 opacity-10">
        <Gift className="w-32 h-32" strokeWidth={1.5} />
      </div>
      <div className="relative">
        <div className="font-mono-label text-[10px] tracking-widest text-white/80">
          🎁 Referral Rewards
        </div>
        <div className="mt-2 font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight">
          Give $25<span className="text-white/70">.</span> Get $25<span className="text-white/70">.</span>
        </div>
        <p className="mt-2 text-sm text-white/85 leading-relaxed max-w-md">
          Share this code with a friend, {firstName}. When they book, they get <strong>$25 off</strong> — and you get <strong>$25 credit</strong> on your next move.
        </p>

        {/* Code block */}
        <div className="mt-5 bg-white/10 backdrop-blur-sm border border-white/25 p-4">
          <div className="font-mono-label text-[9px] tracking-widest text-white/60">Your Code</div>
          <div
            className="mt-1 font-display font-black text-3xl tracking-[0.15em]"
            data-testid="referral-code-display"
          >
            {code}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => copy(code, "Code")}
            className="flex items-center justify-center gap-2 bg-white text-[#151816] px-4 py-3 font-bold text-xs uppercase tracking-widest hover:bg-white/90 transition-colors"
            data-testid="referral-copy-code"
          >
            {copied === "Code" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === "Code" ? "Copied!" : "Copy Code"}
          </button>
          <button
            type="button"
            onClick={nativeShare}
            className="flex items-center justify-center gap-2 bg-[#151816] text-white px-4 py-3 font-bold text-xs uppercase tracking-widest hover:bg-[#2a2f2c] transition-colors"
            data-testid="referral-share-btn"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share Link
          </button>
        </div>

        <div className="mt-3 text-[10px] text-white/60 font-mono-label tracking-wide">
          Credit applied after friend&apos;s move is completed · No expiration
        </div>
      </div>
    </div>
  );
}
